if (!customElements.get('bundle-subscribe')) {
  customElements.define(
    'bundle-subscribe',
    class BundleSubscribe extends HTMLElement {
      connectedCallback() {
        this.compareEl = this.querySelector('[data-bundle-subscribe-compare]');
        this.priceEl = this.querySelector('[data-bundle-subscribe-price]');
        this.perServingEl = this.querySelector('[data-bundle-subscribe-per-serving]');
        this.titleEl = this.querySelector('[data-bundle-subscribe-title]');
        this.perServingPrefix = this.dataset.perServingPrefix || 'Only';
        this.perServingSuffix = this.dataset.perServingSuffix || 'per serving';
        this.titleTemplate =
          this.getAttribute('data-title-template') ||
          this.titleEl?.textContent ||
          'Subscribe & Save {pct}%';

        this._onChange = (event) => this.updateFromDetail(event.detail);
        this._onProgressChange = (event) => this.updatePerServingFromProgress(event.detail);
        document.addEventListener('bundle-builder:change', this._onChange);
        document.addEventListener('bundle-progress:change', this._onProgressChange);

        this.updateTitle(0);
        this.renderDisplayPrices(0);
        this.renderPerServing(this.perServingForQty(0));

        if (window.BundleBuilder) {
          this.updateFromDetail({
            items: window.BundleBuilder.getItems(),
            totals: window.BundleBuilder.getTotals(),
            totalPacks: window.BundleBuilder.getTotalPacks(),
            totalQuantity: window.BundleBuilder.getTotalQuantity(),
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('bundle-builder:change', this._onChange);
        document.removeEventListener('bundle-progress:change', this._onProgressChange);
      }

      /**
       * Selected bag / unit count (1–10).
       * @param {{ totalQuantity?: number, items?: Array<{ quantity?: number }> }} detail
       */
      selectedPackCount(detail = {}) {
        const fromDetail = Number(detail.totalQuantity);
        if (Number.isFinite(fromDetail) && fromDetail > 0) {
          return Math.max(1, Math.round(fromDetail));
        }

        const fromBuilder = Number(window.BundleBuilder?.getTotalQuantity?.() || 0);
        if (fromBuilder > 0) return Math.max(1, Math.round(fromBuilder));

        const items = detail.items || [];
        const sum = items.reduce((total, item) => total + (Number(item.quantity) || 0), 0);
        return sum > 0 ? Math.max(1, Math.round(sum)) : 0;
      }

      /** Parse "$44.95" / "15.95" → cents. */
      parseMoneyToCents(value) {
        const amount = Number(String(value || '').replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(amount) || amount < 0) return 0;
        return Math.round(amount * 100);
      }

      /**
       * Theme-editor prices for the selected pack count.
       * 1–4: fixed tier totals. 5+: unit compare − over-4 discount, × qty.
       * @param {number} qty
       * @returns {{ price: string, compare: string } | null}
       */
      tierPricesFor(qty) {
        const n = Math.max(1, Number(qty) || 0);
        if (n < 1) return null;

        if (n <= 4) {
          const price = (this.getAttribute(`data-qty-${n}-price`) || '').trim();
          if (!price) return null;

          return {
            price,
            compare: (this.getAttribute(`data-qty-${n}-compare`) || '').trim(),
          };
        }

        const unitCompareCents = this.parseMoneyToCents(this.getAttribute('data-qty-1-compare'));
        const discountCents = this.parseMoneyToCents(this.getAttribute('data-qty-over-4-unit-discount'));
        if (!unitCompareCents || !discountCents) return null;

        const unitSaleCents = Math.max(0, unitCompareCents - discountCents);

        return {
          price: this.formatMoney(unitSaleCents * n),
          compare: this.formatMoney(unitCompareCents * n),
        };
      }

      /**
       * Save % for title by bag count: 0–1→19, 2→24, 3→28, 4+→35.
       * @param {number} qty
       * @returns {string}
       */
      savePercentFor(qty) {
        const n = Number(qty) || 0;
        const tier = n <= 1 ? (n <= 0 ? 0 : 1) : Math.min(4, Math.max(2, Math.round(n)));
        const fallbacks = { 0: '19', 1: '19', 2: '24', 3: '28', 4: '35' };
        const fallback = fallbacks[tier] || '19';
        return (this.getAttribute(`data-save-pct-${tier}`) || fallback).replace(/[^\d.]/g, '') || fallback;
      }

      updateTitle(qty) {
        if (!this.titleEl) return;

        const pct = this.savePercentFor(qty);
        const template = this.titleTemplate || 'Subscribe & Save {pct}%';

        if (template.includes('{pct}')) {
          this.titleEl.textContent = template.replace(/\{pct\}/g, pct);
          return;
        }

        if (/\d+\s*%/.test(template)) {
          this.titleEl.textContent = template.replace(/\d+(\.\d+)?(?=\s*%)/, pct);
          return;
        }

        this.titleEl.textContent = `${template.trim()} ${pct}%`;
      }

      /**
       * Unit bag price for subscribe display (matches progress /ea steps).
       * 0–1 → $36, 2 → $34, 3 → $32, 4+ → $29
       * @param {number} qty
       * @returns {string}
       */
      displayUnitPriceFor(qty) {
        const tier = Number(qty) <= 0 ? 1 : Math.min(4, Math.max(1, Math.round(Number(qty) || 0)));
        const progress = document.querySelector('bundle-progress');
        const step = progress?.querySelector(`.bundle-progress__step[data-step="${tier}"]`);
        const raw =
          step?.dataset.price ||
          step?.getAttribute('data-price') ||
          step?.querySelector('.bundle-progress__price')?.textContent ||
          '';
        const match = String(raw).match(/\$[\d,.]+/);
        if (match) return match[0];

        const fallbacks = { 1: '$36.00', 2: '$34.00', 3: '$32.00', 4: '$29.00' };
        return fallbacks[tier] || '$36.00';
      }

      /** Always the 1-pack compare ($44.95). */
      displayComparePrice() {
        return (this.getAttribute('data-qty-1-compare') || this.dataset.emptyCompare || '$44.95').trim();
      }

      renderDisplayPrices(qty) {
        const compare = this.displayComparePrice();
        let price = this.displayUnitPriceFor(qty);

        if (price && !/\/ea/i.test(price)) {
          price = `${price}/ea`;
        }

        if (this.compareEl) {
          this.compareEl.textContent = compare;
          this.compareEl.classList.toggle('is-hidden', !compare);
        }
        if (this.priceEl) this.priceEl.textContent = price;
      }

      updateFromDetail(detail = {}) {
        const totals = detail.totals || window.BundleBuilder?.getTotals?.();
        const items = detail.items || [];
        const hasItems = totals?.hasItems ?? items.length > 0;

        if (!hasItems) {
          this.renderEmpty();
          return;
        }

        const qty = this.selectedPackCount(detail);
        this.updateTitle(qty);
        this.renderDisplayPrices(qty);
        this.renderPerServing(this.perServingForQty(qty));
      }

      updatePerServingFromProgress() {
        const totals = window.BundleBuilder?.getTotals?.();
        const qty = totals?.hasItems ? this.selectedPackCount({ totalQuantity: window.BundleBuilder?.getTotalQuantity?.() }) : 0;
        this.renderPerServing(this.perServingForQty(qty));
      }

      /**
       * Per-serving label by bag count: 0–1 → $1.80, then progress steps 2/3/4.
       * @param {number} qty
       * @returns {string}
       */
      perServingForQty(qty) {
        const n = Number(qty) || 0;
        // 0 and 1 bag stay on the first-step price
        const step = n <= 1 ? 1 : Math.min(4, Math.max(1, Math.round(n)));
        return this.perServingForStep(step) || this.dataset.emptyPerServing || '$1.80';
      }

      perServingForStep(step) {
        const progress = document.querySelector('bundle-progress');
        if (!progress || step < 1) return '';

        const stepEl = progress.querySelector(`.bundle-progress__step[data-step="${step}"]`);
        return (
          stepEl?.dataset.perServing ||
          stepEl?.getAttribute('data-per-serving') ||
          ''
        );
      }

      currentProgressPerServing() {
        const progress = document.querySelector('bundle-progress');
        const level = Number(progress?.dataset?.level || 0);
        if (!progress || level < 1) return this.perServingForStep(1);
        return this.perServingForStep(level);
      }

      renderPerServing(value) {
        if (!this.perServingEl || !value) return;
        const hasServingLabel = /serving/i.test(value);
        const suffix = hasServingLabel ? '' : this.perServingSuffix;
        this.perServingEl.textContent = `${this.perServingPrefix} ${value} ${suffix}`.trim();
      }

      renderEmpty() {
        this.updateTitle(0);
        this.renderDisplayPrices(0);
        this.renderPerServing(this.perServingForQty(0));
      }

      formatMoney(cents) {
        const value = (Number(cents) || 0) / 100;
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'USD',
          }).format(value);
        } catch (error) {
          return `$${value.toFixed(2)}`;
        }
      }
    }
  );
}
